import { fetcher } from '@/lib/utils'
import { useCallback, useEffect } from 'react'
import { preload } from 'swr'
/**
 *
 */
/**
 * Preloads community projects data for a given page and filter.
 *
 * This hook constructs an API request URL based on the provided pagination and filter parameters,
 * and preloads the corresponding data using the `preload` function and a specified `fetcher`.
 *
 * @param pageIndex - The current page index (zero-based) for pagination.
 * @param limit - The number of items to fetch per page.
 * @param filter - The filter to apply to the projects list (e.g., 'featured').
 *
 * @remarks
 * - If the filter is set to `'featured'`, only featured projects will be preloaded.
 * - The hook automatically triggers preloading when any of the parameters change.
 */
export const usePreloadCommunityProjects = (
  pageIndex: number,
  limit: number,
  filter: string,
  shouldPreload: boolean = true,
) => {
  const getKey = useCallback(() => {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: (pageIndex * limit).toString(),
      ...(filter === 'featured' && { is_featured: 'true' }),
    })
    return `/api/projects/v2?${params}`
  }, [pageIndex, limit, filter])

  useEffect(() => {
    if (shouldPreload) {
      preload(getKey(), fetcher)
    }
  }, [getKey, shouldPreload])
}

export const usePreloadUserProjects = (
  pageIndex: number,
  limit: number,
  shouldPreload: boolean = true,
) => {
  const getKey = useCallback(() => {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: (pageIndex * limit).toString(),
    })
    return `/api/me/projects?${params}`
  }, [pageIndex, limit])

  useEffect(() => {
    if (shouldPreload) {
      preload(getKey(), fetcher)
    }
  }, [getKey, shouldPreload])
}

export const usePreloadDynamicI18n = () => {
  const getKey = useCallback(() => {
    return '/api/dynamic-i18n'
  }, [])

  useEffect(() => {
    preload(getKey(), fetcher)
  }, [getKey])
}
